import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, CalendarCheck, Headphones, Inbox, UserCircle2, BellRing,
  FileText, Star, Mail, ListChecks, BarChart3, Smartphone, Mic, BookOpen,
  GitBranch, MessagesSquare, Sparkles, Layers, ShieldCheck, Repeat,
  Megaphone, Users, Phone, Gift, MessageCircle, School, Briefcase, Target,
  HeartPulse, TrendingUp, Brain, Workflow, LayoutGrid, Plug,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { DISCIPLINES } from "@/config/disciplines";
import { INTEGRATIONS } from "@/config/integrations";

/* Each capability category carries its own signature accent — so the page reads
   as five mini-stories instead of one flat grid. */
const CATEGORIES = [
  {
    id: "front-desk",
    label: "Front Desk Ops",
    Icon: HeartPulse,
    lede: "Every call, every booking, every reminder — handled before your front desk has to think about it.",
    fromHex: "#0ea5e9",   // sky-500
    toHex:   "#2563eb",   // blue-600
    pill:    "border-sky-500/30 bg-sky-500/10 text-sky-600 dark:text-sky-300",
    dot:     "bg-sky-500",
  },
  {
    id: "patient-experience",
    label: "Patient Experience",
    Icon: Users,
    lede: "Every touchpoint patients see — branded, friction-free and tied to one record.",
    fromHex: "#a855f7",   // violet-500
    toHex:   "#d946ef",   // fuchsia-500
    pill:    "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300",
    dot:     "bg-fuchsia-500",
  },
  {
    id: "marketing",
    label: "Marketing & Growth",
    Icon: TrendingUp,
    lede: "Fill the funnel, then re-engage every dormant patient — on autopilot.",
    fromHex: "#f97316",   // orange-500
    toHex:   "#f43f5e",   // rose-500
    pill:    "border-orange-500/30 bg-orange-500/10 text-orange-600 dark:text-orange-300",
    dot:     "bg-orange-500",
  },
  {
    id: "intelligence",
    label: "Intelligence",
    Icon: Brain,
    lede: "Calls, charts and KPIs read for you — so you only see what matters.",
    fromHex: "#10b981",   // emerald-500
    toHex:   "#14b8a6",   // teal-500
    pill:    "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300",
    dot:     "bg-emerald-500",
  },
  {
    id: "team",
    label: "Team & Workflow",
    Icon: Workflow,
    lede: "How your team actually gets work done — pipelines, automations, knowledge, training.",
    fromHex: "#6366f1",   // indigo-500
    toHex:   "#8b5cf6",   // violet-500
    pill:    "border-indigo-500/30 bg-indigo-500/10 text-indigo-600 dark:text-indigo-300",
    dot:     "bg-indigo-500",
  },
];

/* Core platform capabilities — every clinic on Clinic OS gets these by default.
   "Products" (Hub / Hub+ / Call IQ AI / AI Workforce / Clinic Pulse / Growth / Academy / Sales Autopilot)
   are the modular add-ons that sit on top. */
const CAPABILITIES = [
  // ── Front Desk Ops ──────────────────────────────────────────────────────────
  { cat: "front-desk", Icon: CalendarCheck, name: "Scheduling",
    desc: "2-way calendar sync with your EMR. Book, move, cancel from anywhere." },
  { cat: "front-desk", Icon: Headphones, name: "AI Receptionist",
    desc: "24/7 inbound call answering. 3 rings, AI picks up, books straight in." },
  { cat: "front-desk", Icon: Inbox, name: "Unified Inbox",
    desc: "Calls · SMS · Email · DMs all in one thread, tied to the patient record." },
  { cat: "front-desk", Icon: Phone, name: "Phone System (Yealink)",
    desc: "Plug in any SIP desk phone — Yealink, Polycom, Cisco — ringing into the CRM with full caller context." },
  { cat: "front-desk", Icon: BellRing, name: "Smart Reminders",
    desc: "Auto SMS/email confirmations + day-before checks that confirm or reschedule." },
  { cat: "front-desk", Icon: ListChecks, name: "Front-Desk Tasks",
    desc: "Tasks routed by AI — priority, owner and due-date auto-assigned." },

  // ── Patient Experience ──────────────────────────────────────────────────────
  { cat: "patient-experience", Icon: UserCircle2, name: "Patient Profile",
    desc: "A 360° record — appointments, tasks, notes, balances, activity timeline." },
  { cat: "patient-experience", Icon: FileText, name: "Digital Forms",
    desc: "Drag-and-drop intake forms. Mobile-friendly · auto-attach to the chart." },
  { cat: "patient-experience", Icon: Star, name: "Reviews",
    desc: "Review requests fire 2h post-visit — timed to your happiest moment." },
  { cat: "patient-experience", Icon: Smartphone, name: "Mobile App",
    desc: "Run your clinic from your phone — inbox, calendar, tasks, calls." },
  { cat: "patient-experience", Icon: Users, name: "Communities",
    desc: "Patient community space — comments, posts, peer support, branded under your clinic." },

  // ── Marketing & Growth ──────────────────────────────────────────────────────
  { cat: "marketing", Icon: Megaphone, name: "Campaign Management",
    desc: "Orchestrate multi-channel campaigns — ads, funnels, broadcasts and journeys, all in one console." },
  { cat: "marketing", Icon: Target, name: "Ads Management",
    desc: "Plan, launch and track Meta + Google ad spend — with patient-level attribution back to bookings." },
  { cat: "marketing", Icon: Mail, name: "Email & SMS Marketing",
    desc: "Campaign builder for recall, reactivation and announcements." },
  { cat: "marketing", Icon: Gift, name: "Referral Program",
    desc: "Trackable patient referrals with rewards — share links, monitor conversions, payout automatically." },
  { cat: "marketing", Icon: Repeat, name: "Reactivation List",
    desc: "Dormant patients auto-surfaced and queued for win-back outreach." },

  // ── Intelligence ────────────────────────────────────────────────────────────
  { cat: "intelligence", Icon: MessageCircle, name: "Live Chat & Wiki",
    desc: "Chatbot, human live chat, knowledge base, public docs and a central library — every answer your patients and team need, in one searchable layer." },
  { cat: "intelligence", Icon: Mic, name: "Call Intelligence",
    desc: "Every call transcribed, classified and audited — booked vs general." },
  { cat: "intelligence", Icon: BarChart3, name: "Analytics & Reporting",
    desc: "Live KPIs · clinician utilisation · channel performance · revenue." },
  { cat: "intelligence", Icon: BookOpen, name: "Manuals · Ask Mathew",
    desc: "An AI knowledge agent that answers your team from your own SOPs." },

  // ── Team & Workflow ─────────────────────────────────────────────────────────
  { cat: "team", Icon: GitBranch, name: "Workflow Automation",
    desc: "Visual no-code builder. Trigger workflows on call · form · tag · time." },
  { cat: "team", Icon: Briefcase, name: "Sales & Pipeline",
    desc: "CRM pipelines for new patients, programs and memberships — stages, owners, conversion tracking." },
  { cat: "team", Icon: MessagesSquare, name: "Team Chat",
    desc: "Internal staff messaging tied to patient threads and tasks." },
  { cat: "team", Icon: School, name: "University & Classroom",
    desc: "Build branded courses, training programs and certification paths — for patients and staff alike." },
];

/* Outcome tiles shown right under the hero — turn the feature list into clinic-owner outcomes. */
const OUTCOMES = [
  { stat: "94%",    label: "of inbound calls answered",   foot: "vs ~60% industry average",     accent: "from-sky-500 to-blue-600" },
  { stat: "37 hrs", label: "saved per clinic, per week",  foot: "front desk + admin combined",  accent: "from-violet-500 to-fuchsia-500" },
  { stat: "3.2×",   label: "more 5-star reviews",          foot: "post-visit reviews on autopilot", accent: "from-amber-500 to-orange-500" },
  { stat: "14 days", label: "to fully go live",            foot: "EMR sync + onboarding included", accent: "from-emerald-500 to-teal-500" },
];

const CAPS_BY_CATEGORY = CATEGORIES.map((c) => ({
  ...c,
  items: CAPABILITIES.filter((cap) => cap.cat === c.id),
}));

export default function Platform() {
  const openDemo = useDemo();
  const [activeCat, setActiveCat] = useState("all");
  const visibleGroups =
    activeCat === "all" ? CAPS_BY_CATEGORY : CAPS_BY_CATEGORY.filter((g) => g.id === activeCat);

  return (
    <article className="overflow-hidden">
      {/* HERO */}
      <section className="relative pt-16 pb-8 sm:pt-28 sm:pb-12">
        <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-[460px] bg-gradient-to-b from-violet-500/15 to-transparent blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          <p className="hidden sm:inline-flex items-center gap-2 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
            The Clinic OS Platform
          </p>
          <h1 className="mt-0 sm:mt-5 max-w-4xl font-display text-[2.05rem] font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Everything your clinic runs on,{" "}
            <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">in one operating system.</span>
          </h1>
          <p className="mt-3 sm:mt-6 max-w-3xl text-[14px] leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
            <strong className="font-bold text-foreground/85">24 core capabilities</strong> baked into Clinic OS — from scheduling to AI calls
            to reviews. Modular <Link to="/products" className="font-semibold text-foreground underline-offset-4 hover:underline">Products</Link>{" "}
            snap on top when you need more. Same login, same data, same calm.
          </p>

          <div className="mt-5 sm:mt-8 flex flex-wrap items-center gap-2 sm:gap-3">
            <Link
              to="/build"
              data-testid="platform-build-cta"
              className="inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-6 text-sm font-bold text-white shadow-lg shadow-violet-600/25 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              <Layers className="h-4 w-4" />
              Build Your OS
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href="#platform-capabilities"
              data-testid="platform-see-modules"
              className="inline-flex h-12 w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30 hover:bg-secondary"
            >
              <LayoutGrid className="h-4 w-4" />
              See all 24 modules
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
            <button
              data-testid="platform-book-demo"
              onClick={openDemo}
              className="hidden sm:inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-border bg-card px-6 text-sm font-semibold transition-all hover:-translate-y-0.5 hover:bg-secondary"
            >
              <CalendarCheck className="h-4 w-4" />
              Book a Demo
            </button>
            <Link
              to="/products"
              data-testid="platform-see-products"
              className="hidden sm:inline-flex h-12 items-center justify-center gap-1 px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              See Add-on Products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Trust strip — desktop only; mobile sticky CTA covers the trust signals */}
          <div className="mt-6 hidden flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted-foreground sm:flex">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> HIPAA &amp; SOC 2 compliant
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>EMR sync &middot; both ways</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>Live in &lt; 14 days</span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span>Cancel anytime</span>
          </div>

          {/* Platform · Products · Integrations explainer — hidden on mobile (sections below cover it) */}
          <div className="mt-8 hidden gap-3 sm:grid sm:gap-4 lg:grid-cols-3">
            <div className="relative overflow-hidden rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/[0.08] via-blue-500/[0.04] to-transparent p-5">
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-violet-500/20 blur-3xl" />
              <div className="relative flex items-center gap-2">
                <Layers className="h-4 w-4 text-violet-500" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
                  Platform · default
                </span>
              </div>
              <p className="relative mt-2 font-display text-lg font-black tracking-tight">
                The foundation
              </p>
              <p className="relative mt-1 text-sm leading-relaxed text-muted-foreground">
                <strong className="font-bold text-foreground/85">24 core capabilities</strong> every Clinic OS account gets. One login, one inbox,
                one patient record. The OS itself.
              </p>
            </div>

            <div className="relative overflow-hidden rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.08] via-teal-500/[0.04] to-transparent p-5">
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-emerald-500/20 blur-3xl" />
              <div className="relative flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-emerald-500" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-emerald-600 dark:text-emerald-400">
                  Products · add-ons
                </span>
              </div>
              <p className="relative mt-2 font-display text-lg font-black tracking-tight">
                Snap-on modules
              </p>
              <p className="relative mt-1 text-sm leading-relaxed text-muted-foreground">
                Hub · Hub+ · Call IQ AI · AI Workforce · Clinic Pulse · Growth · Academy ·
                Sales Autopilot. Turn them on as you grow.
              </p>
            </div>

            {/* Integrations card with mini logo marquee */}
            <Link
              to="/integrations"
              data-testid="platform-integrations-card"
              className="group relative flex flex-col overflow-hidden rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] via-orange-500/[0.04] to-transparent p-5 transition-all hover:-translate-y-0.5 hover:border-amber-500/40 hover:shadow-md"
            >
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-amber-500/20 blur-3xl" />
              <div className="relative flex items-center gap-2">
                <Plug className="h-4 w-4 text-amber-500" />
                <span className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-amber-600 dark:text-amber-400">
                  Integrations · {INTEGRATIONS.length}+ tools
                </span>
              </div>
              <p className="relative mt-2 font-display text-lg font-black tracking-tight">
                Plug into your stack
              </p>

              {/* Mini horizontal marquee */}
              <div
                className="relative mt-3 overflow-hidden"
                style={{
                  maskImage: "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
                  WebkitMaskImage: "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
                }}
              >
                <style>{`
                  @keyframes platform-mini-marquee {
                    from { transform: translate3d(0,0,0); }
                    to   { transform: translate3d(-50%,0,0); }
                  }
                  .platform-mini-track { animation: platform-mini-marquee 40s linear infinite; }
                  .group:hover .platform-mini-track { animation-play-state: paused; }
                `}</style>
                <div className="platform-mini-track flex w-max items-center gap-2">
                  {[...INTEGRATIONS, ...INTEGRATIONS].map((it, i) => (
                    <span
                      key={`${it.name}-${i}`}
                      className="inline-flex h-8 w-8 flex-none items-center justify-center rounded-md bg-white shadow-sm ring-1 ring-inset ring-black/5"
                      title={it.name}
                    >
                      <img
                        src={it.logo}
                        alt={it.name}
                        loading="lazy"
                        className="h-4 w-4 object-contain"
                        onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                      />
                    </span>
                  ))}
                </div>
              </div>

              <span className="relative mt-auto inline-flex items-center gap-1 pt-3 text-[12.5px] font-bold text-amber-700 dark:text-amber-400">
                See all integrations
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* (Two-reasons DiscChapters section removed per request) */}

      {/* OUTCOMES STRIP — turn the feature list into clinic-owner outcomes */}
      <section data-testid="platform-outcomes" className="border-t border-border py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div className="max-w-xl">
              <p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-300">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                What clinics actually see
              </p>
              <h2 className="mt-3 font-display text-2xl font-black leading-[1.06] tracking-tight sm:text-3xl lg:text-4xl">
                Numbers from real Clinic OS rollouts.
              </h2>
            </div>
            <p className="text-[12px] uppercase tracking-[0.22em] text-muted-foreground">
              Averaged across active accounts · last 90 days
            </p>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {OUTCOMES.map((o, i) => (
              <motion.div
                key={o.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                data-testid={`platform-outcome-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg sm:p-6"
              >
                <div className={`pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-gradient-to-br ${o.accent} opacity-[0.12] blur-3xl transition-opacity group-hover:opacity-30`} />
                <p className={`relative bg-gradient-to-r ${o.accent} bg-clip-text font-display text-3xl font-black tracking-tight text-transparent sm:text-4xl lg:text-5xl`}>
                  {o.stat}
                </p>
                <p className="relative mt-2 text-[13px] font-semibold leading-tight text-foreground/90 sm:text-sm">
                  {o.label}
                </p>
                <p className="relative mt-1 text-[11.5px] leading-snug text-muted-foreground">
                  {o.foot}
                </p>
                <div className={`absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r ${o.accent} transition-transform duration-500 group-hover:scale-x-100`} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES — grouped by category, each with its own signature accent */}
      <section id="platform-capabilities" data-testid="platform-capabilities" className="border-t border-border py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
              All capabilities · included by default
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              Everything your front desk and front line{" "}
              <span className="bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">need.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Grouped into five core areas of clinic life. Tap a category to focus, or keep <span className="font-semibold text-foreground/85">All</span> to see everything.
            </p>
          </div>

          {/* Filter chips — single line, scrollable on mobile */}
          <div className="-mx-6 mt-7 overflow-x-auto px-6 sm:mx-0 sm:overflow-visible sm:px-0">
            <div className="flex w-max items-center gap-2 sm:w-auto sm:flex-nowrap" data-testid="platform-cat-chips">
                {/* All chip */}
                <button
                  type="button"
                  onClick={() => setActiveCat("all")}
                  data-testid="platform-cat-chip-all"
                  aria-pressed={activeCat === "all"}
                  className={
                    activeCat === "all"
                      ? "group inline-flex flex-none items-center gap-1.5 rounded-full bg-foreground px-3.5 py-1.5 text-[11.5px] font-bold tracking-tight text-background shadow-md transition-all"
                      : "group inline-flex flex-none items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-1.5 text-[11.5px] font-semibold tracking-tight text-foreground/80 transition-all hover:-translate-y-0.5 hover:border-foreground/40 hover:text-foreground"
                  }
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  All
                  <span className="text-[10px] font-bold opacity-70">· {CAPABILITIES.length}</span>
                </button>

                {CAPS_BY_CATEGORY.map((g) => {
                  const CIcon = g.Icon;
                  const isActive = activeCat === g.id;
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => setActiveCat(g.id)}
                      data-testid={`platform-cat-chip-${g.id}`}
                      aria-pressed={isActive}
                      style={isActive ? { backgroundImage: `linear-gradient(135deg, ${g.fromHex}, ${g.toHex})` } : undefined}
                      className={
                        isActive
                          ? "group inline-flex flex-none items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11.5px] font-bold tracking-tight text-white shadow-md ring-1 ring-inset ring-white/15 transition-all"
                          : `group inline-flex flex-none items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[11.5px] font-semibold tracking-tight transition-all hover:-translate-y-0.5 ${g.pill}`
                      }
                    >
                      <CIcon className="h-3.5 w-3.5" />
                      {g.label}
                      <span className="text-[10px] font-bold opacity-70">· {g.items.length}</span>
                    </button>
                  );
                })}
            </div>
          </div>

          {/* Category sections — animated swap */}
          <div className="mt-12 space-y-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCat}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-14"
              >
                {visibleGroups.map((group) => {
                  const GIcon = group.Icon;
                  return (
                    <section
                      key={group.id}
                      id={`cap-${group.id}`}
                      data-testid={`platform-cat-${group.id}`}
                      className="scroll-mt-24"
                    >
                      {/* Compact, single-line category header */}
                      <div className="flex flex-wrap items-center gap-3 border-b border-border pb-4 sm:flex-nowrap">
                        <span
                          className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15"
                          style={{ backgroundImage: `linear-gradient(135deg, ${group.fromHex}, ${group.toHex})` }}
                        >
                          <GIcon className="h-[18px] w-[18px]" />
                        </span>
                        <h3
                          className="font-display text-[17px] font-black tracking-tight sm:text-lg"
                          style={{ color: group.fromHex }}
                        >
                          {group.label}
                        </h3>
                        <p className="hidden min-w-0 flex-1 truncate text-[13px] leading-snug text-muted-foreground sm:block">
                          — {group.lede}
                        </p>
                        <span className="ml-auto flex-none text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                          {group.items.length} {group.items.length === 1 ? "capability" : "capabilities"}
                        </span>
                      </div>

                      {/* Capability cards */}
                      <div className="mt-6 grid gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {group.items.map((c, i) => (
                          <motion.div
                            key={c.name}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.28, delay: i * 0.02 }}
                            data-testid={`platform-capability-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-foreground/25 hover:shadow-lg"
                          >
                            <div
                              className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-40"
                              style={{ backgroundImage: `linear-gradient(135deg, ${group.fromHex}, ${group.toHex})` }}
                            />
                            <span
                              className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm ring-1 ring-inset ring-white/15 transition-transform group-hover:scale-110"
                              style={{ backgroundImage: `linear-gradient(135deg, ${group.fromHex}, ${group.toHex})` }}
                            >
                              <c.Icon className="h-5 w-5" />
                            </span>
                            <p className="relative mt-3.5 font-display text-[15px] font-black tracking-tight sm:text-base">
                              {c.name}
                            </p>
                            <p className="relative mt-1.5 text-[12.5px] leading-relaxed text-muted-foreground sm:text-[13px]">
                              {c.desc}
                            </p>
                            <div
                              className="absolute inset-x-0 bottom-0 h-[2.5px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
                              style={{ backgroundImage: `linear-gradient(90deg, ${group.fromHex}, ${group.toHex})` }}
                            />
                          </motion.div>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Trust strip */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 rounded-2xl border border-border bg-gradient-to-r from-secondary/40 via-card to-secondary/40 px-6 py-4 text-[12.5px] text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span className="font-semibold text-foreground/85">HIPAA &amp; SOC 2</span> compliant
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />
              EMR sync &middot; both directions
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-violet-500" />
              Setup in &lt; 14 days
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-rose-500" />
              Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* DISCIPLINES */}
      <section className="relative overflow-hidden border-t border-border bg-secondary/30 py-20 sm:py-24 dark:bg-transparent">
        <div className="pointer-events-none absolute -right-32 top-10 h-72 w-72 rounded-full bg-violet-500/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-32 bottom-10 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-[0.22em] text-emerald-600 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              Built for your clinic
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.06] tracking-tight sm:text-4xl lg:text-5xl">
              See Clinic OS for your{" "}
              <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 bg-clip-text text-transparent">specialty.</span>
            </h2>
            <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted-foreground sm:text-base">
              Same platform, tuned to the language and workflows of your discipline.
            </p>
          </div>
          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DISCIPLINES.map((d, i) => {
              const DIcon = d.icon;
              return (
                <motion.div
                  key={d.slug}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.35, delay: i * 0.04 }}
                >
                  <Link
                    to={`/disciplines/${d.slug}`}
                    data-testid={`platform-discipline-${d.slug}`}
                    className="group relative flex items-center gap-3 overflow-hidden rounded-xl border border-border bg-card px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-md"
                  >
                    <div className={`pointer-events-none absolute -right-12 -top-12 h-28 w-28 rounded-full ${d.bg.replace("/12", "/30")} opacity-0 blur-3xl transition-opacity group-hover:opacity-100`} />
                    <span className={`relative inline-flex h-10 w-10 flex-none items-center justify-center rounded-lg ${d.bg} ${d.accent} ring-1 ring-inset ring-foreground/[0.04]`}>
                      <DIcon className="h-[18px] w-[18px]" />
                    </span>
                    <span className="relative font-display text-[14px] font-bold tracking-tight">{d.name}</span>
                    <ArrowRight className="relative ml-auto h-4 w-4 text-muted-foreground transition-all group-hover:translate-x-0.5 group-hover:text-foreground" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA — dark cinematic banner matching home-page FinalCTA */}
      <section className="border-t border-border bg-gradient-to-b from-white via-slate-50/60 to-slate-100/60 py-16 dark:bg-none dark:from-transparent dark:via-transparent dark:to-transparent sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="relative overflow-hidden rounded-3xl border border-border bg-[#0a0a14] px-8 py-16 text-center sm:px-16 sm:py-24">
            <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-violet-600/30 blur-[120px]" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-blue-600/25 blur-[120px]" />
            <div className="pointer-events-none absolute inset-0 bg-grid opacity-30" />
            <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/5" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                One platform · 24 capabilities · 8 modular products
              </span>

              <h2 className="mt-6 font-display text-4xl font-black leading-[1.05] tracking-[-0.025em] text-white sm:text-6xl lg:text-7xl">
                Run your clinic.{" "}
                <span className="relative inline-block whitespace-nowrap">
                  <span className="text-white/40">Not your</span>{" "}
                  <span className="relative inline-block text-rose-400">
                    tools
                    <motion.span
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.35, ease: "easeOut" }}
                      className="absolute inset-x-0 top-1/2 h-[4px] origin-left -translate-y-1/2 rounded-full bg-rose-400 sm:h-[5px]"
                      style={{ transformOrigin: "left" }}
                      aria-hidden="true"
                    />
                  </span>
                  <span className="text-white/40">.</span>
                </span>
              </h2>

              <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg">
                Replace the messy stack with one calm OS. Fill the schedule, engage every
                patient, keep them coming back — without the tab-switching tax.
              </p>

              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <Link
                  to="/build"
                  data-testid="platform-final-cta-build"
                  className="group inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 px-8 text-sm font-bold text-white shadow-lg shadow-violet-600/40 ring-1 ring-inset ring-white/10 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-violet-600/50 sm:w-auto"
                >
                  Build Your OS <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
                <button
                  type="button"
                  data-testid="platform-final-cta-demo"
                  onClick={openDemo}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-8 text-sm font-semibold text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:border-white/30 hover:bg-white/10 sm:w-auto"
                >
                  <CalendarCheck className="h-4 w-4" /> Book a Demo
                </button>
              </div>

              <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-white/55">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                  HIPAA-ready
                </span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>SOC 2 aligned</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>99.9% uptime</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span>2-way EMR sync</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </article>
  );
}
